import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AppUser, Role } from '@/types';

type UsersResponse = { users: AppUser[] };

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await api.get<UsersResponse>('/users');
      return data.users;
    },
  });
}

export type CreateUserInput = {
  email: string;
  displayName: string;
  role: Role;
  department?: string;
  sendInvite: boolean;
};

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const { data } = await api.post('/users', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export type UpdateUserInput = {
  uid: string;
  displayName?: string;
  role?: Role;
  department?: string;
  disabled?: boolean;
};

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ uid, ...patch }: UpdateUserInput) => {
      const { data } = await api.patch(`/users/${uid}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uid: string) => {
      const { data } = await api.delete(`/users/${uid}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
